import { combineLatest, map, Observable, of } from 'rxjs';
import { Injectable, signal } from '@angular/core';
import { getOffsetDate } from '@app/core/util/date_utils';
import { IAlertTestItem } from '../interfaces/notifications-data.interfaces';

@Injectable({
  providedIn: 'root',
})
export class NotificationsDataService {
  /** Shared alerts state – single source of truth for desktop and mobile. */
  private _alerts = signal<IAlertTestItem[]>(this._buildInitialAlerts());
  private _notifications = signal<IAlertTestItem[]>(
    this._buildInitialNotifications(),
  );

  readonly alerts = this._alerts.asReadonly();
  readonly notifications = this._notifications.asReadonly();

  markAllAlertsAsRead(): void {
    this._alerts.update((list: IAlertTestItem[]) =>
      list.map((a: IAlertTestItem) => ({
        ...a,
        read_at: a.read_at ?? new Date(),
      })),
    );
  }

  markAllNotificationsAsRead(): void {
    this._notifications.update((list: IAlertTestItem[]) =>
      list.map((n: IAlertTestItem) => ({
        ...n,
        read_at: n.read_at ?? new Date(),
      })),
    );
  }

  archiveAllReadAlerts(): void {
    this._alerts.update((list: IAlertTestItem[]) =>
      list.map((a: IAlertTestItem) =>
        a.read_at && !a.archived_at ? { ...a, archived_at: new Date() } : a,
      ),
    );
  }

  archiveAllReadNotifications(): void {
    this._notifications.update((list: IAlertTestItem[]) =>
      list.map((n: IAlertTestItem) =>
        n.read_at && !n.archived_at ? { ...n, archived_at: new Date() } : n,
      ),
    );
  }

  archiveAllAlerts(): void {
    const date: Date = new Date();

    this._alerts.update((list: IAlertTestItem[]) =>
      list.map((a: IAlertTestItem) => ({
        ...a,
        archived_at: a.archived_at ?? date,
        read_at: a.read_at ?? date,
      })),
    );
  }

  archiveAllNotifications(): void {
    const date: Date = new Date();

    this._notifications.update((list: IAlertTestItem[]) =>
      list.map((n: IAlertTestItem) => ({
        ...n,
        archived_at: n.archived_at ?? date,
        read_at: n.read_at ?? date,
      })),
    );
  }

  toggleAlertMarkAsRead(alert: IAlertTestItem): void {
    if (!alert) {
      return;
    }

    const isRead: boolean = !!alert.read_at;

    this._alerts.update((list: IAlertTestItem[]) =>
      list.map((a: IAlertTestItem) =>
        a.id === alert.id ? { ...a, read_at: isRead ? null : new Date() } : a,
      ),
    );
  }

  toggleNotificationMarkAsRead(notification: IAlertTestItem): void {
    if (!notification) {
      return;
    }

    const isRead: boolean = !!notification.read_at;

    this._notifications.update((list: IAlertTestItem[]) =>
      list.map((n: IAlertTestItem) =>
        n.id === notification.id
          ? { ...n, read_at: isRead ? null : new Date() }
          : n,
      ),
    );
  }

  archiveAlert(alert: IAlertTestItem): void {
    if (!alert) {
      return;
    }

    const date: Date = new Date();

    this._alerts.update((list: IAlertTestItem[]) =>
      list.map((a: IAlertTestItem) =>
        a.id === alert.id ? { ...a, archived_at: date, read_at: date } : a,
      ),
    );
  }

  archiveNotification(notification: IAlertTestItem): void {
    if (!notification) {
      return;
    }

    const date: Date = new Date();

    this._notifications.update((list: IAlertTestItem[]) =>
      list.map((n: IAlertTestItem) =>
        n.id === notification.id
          ? { ...n, archived_at: date, read_at: date }
          : n,
      ),
    );
  }

  markAlertAsRead(alert: IAlertTestItem): void {
    if (!alert) {
      return;
    }

    this._alerts.update((list: IAlertTestItem[]) =>
      list.map((a: IAlertTestItem) =>
        a.id === alert.id ? { ...a, read_at: new Date() } : a,
      ),
    );
  }

  markNotificationAsRead(notification: IAlertTestItem): void {
    if (!notification) {
      return;
    }

    this._notifications.update((list: IAlertTestItem[]) =>
      list.map((n: IAlertTestItem) =>
        n.id === notification.id ? { ...n, read_at: new Date() } : n,
      ),
    );
  }

  // private methods
  private _buildInitialAlerts(): IAlertTestItem[] {
    return [
      {
        id: '1',
        title: 'Alert 1, pallet data has been updated',
        body: 'A new file updates 15 pallets with new values. Review updates. This is a long alert body to test the overflow of the alert menu. ',
        read_at: null,
        sent_at: new Date(),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '2',
        title:
          'Alert 2. Long, title, long title, long title, very, very long title, very, very long title',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: new Date(),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/vessel.svg',
        targetUrl: '/account/dashboard',
      },
      {
        id: '3',
        title: 'Short title',
        body: 'Short body ',
        read_at: null,
        sent_at: getOffsetDate(1),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '4',
        title: 'Long title, long title, long title, long title',
        body: 'Short body ',
        read_at: null,
        sent_at: getOffsetDate(1),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/vessel.svg',
        targetUrl: '/account/dashboard',
      },
      {
        id: '5',
        title: 'Alert 5',
        body: 'Long long long long long long body',
        read_at: null,
        sent_at: getOffsetDate(2),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/vessel.svg',
        targetUrl: '/account/dashboard',
      },
      {
        id: '6',
        title: 'Alert 6',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '7',
        title: 'Alert 7',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '8',
        title: 'Alert 8',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '9',
        title: 'Alert 9',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '10',
        title: 'Alert 10',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
      {
        id: '11',
        title: 'Alert 11',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/stock',
      },
    ];
  }

  private _buildInitialNotifications(): IAlertTestItem[] {
    return [
      {
        id: '12',
        title: 'Notification 12, order data has been updated',
        body: 'There seems to be a change in the order status. Review the order details. ',
        read_at: null,
        sent_at: new Date(),
        archived_at: null,
        iconPath: '/assets/icons/general/category.svg',
        targetUrl: '/account/orders',
      },
      {
        id: '13',
        title: 'Notification 13, shipment data has been updated',
        body: 'There seems to be a change in the shipment status. Review the shipment details. ',
        read_at: null,
        sent_at: new Date(),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/vessel.svg',
        targetUrl: '/account/shipments',
      },
      {
        id: '14',
        title:
          'Notification 14, ocean load out instruction data has been updated',
        body: 'Short body ',
        read_at: null,
        sent_at: getOffsetDate(1),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/load-out-instructions/ocean',
      },
      {
        id: '15',
        title: 'Long title, long title, long title, long title',
        body: 'Short body ',
        read_at: null,
        sent_at: getOffsetDate(1),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/vessel.svg',
        targetUrl: '/account/dashboard',
      },
      {
        id: '16',
        title:
          'Notification 15, arrival QC data has been updated, with some more, more text',
        body: 'There seems to be a change in the arrival QC status. Review the arrival QC details. Actually, there is a lot of text here to test the overflow of the notification menu. ',
        read_at: null,
        sent_at: getOffsetDate(2),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/vessel.svg',
        targetUrl: '/account/arrival-qc',
      },
      {
        id: '17',
        title: 'Notification 17, payment data has been updated',
        body: 'A new file updates 15 pallets with new values. Review updates ',
        read_at: null,
        sent_at: getOffsetDate(3),
        archived_at: null,
        iconPath: 'assets/icons/dashboard/cards/pallet.svg',
        targetUrl: '/account/payments',
      },
    ];
  }

  // notification rules
  public getRules$(): Observable<any[]> {
    return of([
      {
        id: 1,
        name: 'Pallet data has changed and need to be reviewed',
        description:
          'Notify me when information such as the buyer, target region, target market etc. changes on pallets.',
        tag: 'data-updates,A',
        severity: 'alert',
      },
      {
        id: 2,
        name: 'Pallets have been standing at a location for more than',
        description: 'Other was updated',
        tag: 'data-updates,A',
        severity: 'informative',
      },
      {
        id: 3,
        name: 'Successful transhipments',
        description:
          'Notify me when my containers have successfully been transhipped.',
        tag: 'data-updates,B',
        severity: 'informative',
      },
      {
        id: 4,
        name: 'Missed transhipments',
        description:
          'Notify me when my containers have been missed during transhipment.',
        tag: 'data-updates,B',
        severity: 'alert',
      },
      {
        id: 5,
        name: 'Stack date updates',
        description:
          'Notify me when the stack dates have changed at ports my containers are being discharged at.',
        tag: 'data-updates,B',
        severity: 'informative',
      },
    ]);
  }

  getNotificationSubscriptionRules$(): Observable<any[]> {
    return of([
      {
        id: 1,
        notificationRuleId: 2,
        duration: '1_day',
        enabled: true,
        notify_email: false,
        notify_web: false,
        notify_whatsapp: false,
      },
      {
        id: 2,
        notificationRuleId: 4,
        enabled: false,
        notify_email: true,
        notify_web: true,
        notify_whatsapp: false,
      },
      {
        id: 3,
        notificationRuleId: 3,
        enabled: true,
        notify_email: false,
        notify_web: false,
        notify_whatsapp: false,
      },
    ]);
  }

  getRulesWithSubscriptions$(): Observable<any[]> {
    return combineLatest([
      this.getRules$(),
      this.getNotificationSubscriptionRules$(),
    ]).pipe(
      map(([rules, subscriptions]) => {
        return rules.map((rule) => {
          const subscription = subscriptions.find(
            (sub) => sub.notificationRuleId === rule.id,
          );
          return {
            ...rule,
            subscription: subscription || {
              enabled: false,
              notify_email: false,
              notify_web: false,
              notify_whatsapp: false,
            },
          };
        });
      }),
    );
  }

  // channel notifications
  public getNotifications$(_channel = 'platform'): Observable<any[]> {
    return of([
      // TODO: replace with actual channel notifications
      {
        id: 1,
        notificationItems: [
          {
            Pallet: {
              id: 1,
              entityType: 'Pallet',
              palletNumber: '1234567890',
            },
          },
        ],
        notificationRule: {
          id: 1,
          name: 'Pallet',
          description: 'Pallet was updated',
          tag: 'data-and-tracking',
        },
      },
      {
        id: 2,
        notificationItems: [
          {
            entity: {
              id: 2,
              entityType: 'Pallet',
              palletNumber: '1234567890',
            },
          },
        ],
        notificationRule: {
          id: 1,
          name: 'Other',
          description: 'Pallet was updated',
          tag: 'data-and-tracking',
        },
      },
    ]);
  }
}
